import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import * as yup from 'yup';

import Button from '../../../components/form/button/Button';
import Input from '../../../components/form/input/Input';
import Icon from '../../../components/icon/Icon';
import Header from '../../components/header/Header';
import { login, loginExternal, silentLogin } from '../../../core/state/auth/asyncActions';
import { LoginFormData } from '../../../core/state/auth/types';
import { internalDomainNames } from '../../../core/constants/domains';
import { AuthStatus } from '../../../core/enums/AuthStatus';
import { HOME_ROUTE } from '../../../core/constants/routes';
import { RootState } from '../../../core/state/state';
import { spawnGlobalNotification } from '../../../core/state/global-notifications/asyncActions';
import AuthService from '../../../core/services/AccountService';
import Image from '../../../components/image/Image';
import logo from '../../../../img/login-logo.svg';
import { createErrorNotificationContent } from '../LoginErrorMessage';
import { tokenRequest } from '../../../core/constants/login/loginConfig';

import './LoginMobileScreen.scss';

const LoginMobileScreen: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const status = useSelector((state: RootState) => state.auth.status);
  const [isExternalForm, setIsExternalForm] = React.useState(false);
  const projectId = useSelector((state: RootState) => state.user.userInfo?.projectID);
  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const { instance, inProgress, accounts } = useMsal();
  const [apiData, setApiData] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!apiData && inProgress === InteractionStatus.None && accounts.length > 0) {
      const accessTokenRequest = {
        ...tokenRequest,
        account: accounts[0]
      };

      instance
        .acquireTokenSilent(accessTokenRequest)
        .then(async (accessTokenResponse) => {
          setApiData(accessTokenResponse.accessToken);
          await dispatch(silentLogin());
        })
        .catch(() => undefined);
    }
  }, [instance, accounts, inProgress, dispatch, apiData]);

  const validationSchema = yup
    .object()
    .shape({
      email: yup.string().required(t('validation.email.required')).email(t('validation.email.invalidFormat')).min(2),
      password: yup.string()
    })
    .required();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: {
      email: '',
      password: ''
    },
    resolver: yupResolver(validationSchema)
  });

  React.useEffect(() => {
    const email = query.get('email');
    if (email) {
      setValue('email', email);
      setIsExternalForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const email = watch('email');
  const password = watch('password');

  if (status === AuthStatus.Authenticated && typeof projectId === 'number') {
    return <Redirect to={`/projects/${projectId}/bookings`} />;
  }
  if (status === AuthStatus.Authenticated && typeof projectId !== 'number') {
    return <Redirect to={HOME_ROUTE} />;
  }

  if (inProgress === InteractionStatus.HandleRedirect || apiData) {
    return null;
  }

  const onSubmit = handleSubmit(async (data: LoginFormData) => {
    const isInternalUser = internalDomainNames.some((domain) => email.includes(domain));

    if (isInternalUser) {
      dispatch(login());
    } else if (!isInternalUser && !password) {
      const response = await AuthService.checkEmail(data.email);

      if (!response.data.result.result) {
        dispatch(spawnGlobalNotification(createErrorNotificationContent(response, t)));
      } else {
        setIsExternalForm(true);
        clearErrors('email');
      }
    } else {
      dispatch(loginExternal(data, t));
    }
  });

  const resetForm = () => {
    setValue('password', '');
    setIsExternalForm(false);
  };

  const getContent = () =>
    !isExternalForm ? (
      <Controller
        name="email"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Input
            type="string"
            inputMode="email"
            className="login-mobile-screen__form-input"
            onChange={(e) => {
              onChange(e.target.value);
            }}
            id="email"
            error={errors.email?.message}
            name="email"
            label={t('login.field.email.label')}
            placeholder={t('login.field.email.placeholder')}
            value={value}
          />
        )}
      />
    ) : (
      <>
        <div />
        <Controller
          name="password"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Input
              type="string"
              className="login-mobile-screen__form-input"
              onChange={(e) => {
                onChange(e.target.value);
              }}
              id="password"
              error={errors.password?.message}
              name="password"
              label={t('login.field.password.label')}
              placeholder={t('login.field.password.placeholder')}
              value={value}
            />
          )}
        />
      </>
    );

  return (
    <div className="login-mobile-screen">
      <Header />
      <form className="login-mobile-screen__form" onSubmit={onSubmit}>
        <div>
          <div className="d-flex justify-content-center">
            <Image className="login-mobile-screen__logo" src={logo} alt="logo" />
          </div>
          <div className="login-mobile-screen__form-title">{t('login.title')}</div>
          {getContent()}

          <div className="pt-4">
            {!isExternalForm ? (
              <div className="login-mobile-screen__footer">
                <Button onClick={() => onSubmit()} className="login-mobile-screen__button" buttonTypes={['blue', 'block']}>
                  <b>{t('common.next')}</b>
                  <Icon className="login-mobile-screen__button-icon" icon="arrowRight" size="extra-small" />
                </Button>
              </div>
            ) : (
              <div className="login-mobile-screen__footer d-flex justify-content-between">
                <Button
                  type="button"
                  className="login-mobile-screen__button mr-1"
                  onClick={() => resetForm()}
                  buttonTypes={['blue-outline', 'block']}
                >
                  <Icon className="login__button-icon-back" icon="arrow" size="extra-small" svg />
                  {t('common.back')}
                </Button>
                <Button className="login-mobile-screen__button ml-1" buttonTypes={['blue', 'block']} type="submit">
                  <b>{t('common.login')}</b>
                  <Icon className="login-mobile-screen__button-icon" icon="arrowRight" size="extra-small" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginMobileScreen;
